/**
 * ChangePlanModal — upgrade now (prorated) or downgrade at period end.
 * Preview via POST /subscriptions/change-plan/preview; upgrade pays via
 * /subscriptions/upgrade-checkout; downgrade schedules via /subscriptions/downgrade.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, ArrowUpRight, ArrowDownRight, AlertTriangle, Minus, Plus, Undo2, CheckCircle2 } from 'lucide-react';
import { useSubscription } from '../../SubscriptionContext';
import { useAuth } from '../../AuthContext';
import { payTenantInvoice } from '../../shell/razorpay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const TIERS = [{ code: 'clinic', label: 'Clinic' }, { code: 'chain', label: 'Chain' }];

export default function ChangePlanModal({ open, onClose }) {
  const sub = useSubscription();
  const { user } = useAuth();
  const v2 = sub?.v2 || {};
  const [tier, setTier] = useState('clinic');
  const [ec, setEc] = useState(0);
  const [eu, setEu] = useState(0);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');

  useEffect(() => {
    if (!open) return;
    setTier(v2.tier === 'chain' ? 'chain' : 'clinic');
    setEc(v2.extra_clinics_count || 0); setEu(v2.extra_users_count || 0);
    setDone(''); setErr('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      axios.post(`${API}/subscriptions/change-plan/preview`, { tier, extra_clinics_count: ec, extra_users_count: eu })
        .then((r) => { setPreview(r.data); setErr(''); })
        .catch((e) => { setPreview(null); setErr(e?.response?.data?.detail || 'Could not price this change'); });
    }, 250);
    return () => clearTimeout(t);
  }, [open, tier, ec, eu]);

  if (!open) return null;
  const scheduled = v2.downgrade_scheduled;
  const finish = (msg) => { setDone(msg); setBusy(false); sub.refresh && sub.refresh(); };

  const upgrade = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/subscriptions/upgrade-checkout`, { tier, extra_clinics_count: ec, extra_users_count: eu });
      if (!r.data.razorpay_configured) { setErr('Payment gateway is not configured on this environment.'); setBusy(false); return; }
      await payTenantInvoice(r.data, {
        user, description: `Upgrade · prorated ${r.data.prorated_days} days`,
        onDone: () => finish('Upgrade complete — your new plan is active now.'),
        onError: (m) => { setErr(m); setBusy(false); }, onDismiss: () => setBusy(false),
      });
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not start the upgrade'); setBusy(false); }
  };
  const downgrade = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/subscriptions/downgrade`, { tier, extra_clinics_count: ec, extra_users_count: eu });
      finish(`Downgrade scheduled for ${fmtDate(preview?.effective_at)}.`);
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not schedule the downgrade'); setBusy(false); }
  };
  const cancelDowngrade = async () => {
    setBusy(true); setErr('');
    try { await axios.post(`${API}/subscriptions/cancel-downgrade`); finish('Scheduled downgrade cancelled.'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not cancel'); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/60 p-4" data-testid="change-plan-modal" data-readonly-safe="true">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-auto p-6 relative">
        <button onClick={onClose} className="absolute right-3 top-3 text-slate-400 hover:text-slate-700" data-testid="change-plan-close" aria-label="Close"><X size={16} /></button>
        <h3 className="text-base font-bold text-slate-900">Change plan</h3>
        <p className="text-[12px] text-slate-500 mt-0.5">Upgrades apply now (you pay only for the days left). Downgrades apply at the end of your billing period, {fmtDate(v2.current_period_end)}.</p>

        {scheduled && (
          <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] text-violet-900 flex items-center justify-between gap-2" data-testid="change-plan-scheduled">
            <span>Downgrade to <b className="capitalize">{scheduled.to}</b> scheduled for <b>{fmtDate(scheduled.at)}</b>{scheduled.blocked && ' · waiting for you to reduce users/locations'}</span>
            <button onClick={cancelDowngrade} disabled={busy} data-testid="change-plan-cancel-downgrade" className="inline-flex items-center gap-1 font-semibold underline disabled:opacity-50"><Undo2 size={12} /> Cancel</button>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          {TIERS.map((t) => (
            <button key={t.code} onClick={() => setTier(t.code)} data-testid={`change-plan-tier-${t.code}`}
              className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${tier === t.code ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' : 'border-slate-200 hover:bg-slate-50'}`}>
              <div className="text-[13px] font-bold text-slate-900">{t.label}{v2.tier === t.code && <span className="ml-1.5 text-[10px] font-semibold text-slate-500">(current)</span>}</div>
            </button>
          ))}
        </div>
        <div className="mt-3 space-y-2">
          <Stepper label="Extra locations" value={ec} onChange={setEc} testid="change-plan-locations" />
          <Stepper label="Extra users" value={eu} onChange={setEu} testid="change-plan-users" />
        </div>

        {done && <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800" data-testid="change-plan-done"><CheckCircle2 size={14} />{done}</div>}
        {err && <div className="mt-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-800" data-testid="change-plan-error"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{err}</div>}
        {!done && preview && <PreviewBody p={preview} busy={busy} onUpgrade={upgrade} onDowngrade={downgrade} />}
      </div>
    </div>
  );
}

function Stepper({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-slate-700">{label}</span>
      <div className="inline-flex items-center gap-2">
        <button onClick={() => onChange(Math.max(0, value - 1))} data-testid={`${testid}-minus`} className="w-7 h-7 rounded border border-slate-300 flex items-center justify-center hover:bg-slate-50"><Minus size={12} /></button>
        <span className="w-6 text-center font-semibold tabular-nums" data-testid={`${testid}-value`}>{value}</span>
        <button onClick={() => onChange(value + 1)} data-testid={`${testid}-plus`} className="w-7 h-7 rounded border border-slate-300 flex items-center justify-center hover:bg-slate-50"><Plus size={12} /></button>
      </div>
    </div>
  );
}

function PreviewBody({ p, busy, onUpgrade, onDowngrade }) {
  const limits = <div className="text-[11.5px] text-slate-500 mt-1">New limits: {p.new_location_limit} location{p.new_location_limit === 1 ? '' : 's'} · {p.new_user_limit} users · {inr(p.new_total)} per {String(p.billing_cycle || '').replace('_', ' ')} cycle</div>;
  if (p.kind === 'none') return <div className="mt-4 text-[12.5px] text-slate-500" data-testid="change-plan-none">This is your current plan.</div>;
  if (p.kind === 'upgrade') {
    return (
      <div className="mt-4 rounded-xl border border-slate-200 p-4" data-testid="change-plan-upgrade">
        <div className="text-[12px] font-bold text-indigo-700 flex items-center gap-1"><ArrowUpRight size={13} /> Upgrade · applies immediately</div>
        {limits}
        <table className="w-full mt-3 text-[12.5px]"><tbody>
          {p.line_items.map((li, i) => (
            <tr key={i} data-testid={`change-plan-line-${li.code}`}><td className="py-1 text-slate-700">{li.description}</td><td className="py-1 text-right tabular-nums">{inr(li.amount)}</td></tr>
          ))}
          <tr className="border-t border-slate-200"><td className="py-1 text-slate-500">GST {p.gst_percent}%</td><td className="py-1 text-right tabular-nums">{inr(p.gst_amount)}</td></tr>
          <tr className="font-bold"><td className="py-1">Pay now</td><td className="py-1 text-right tabular-nums" data-testid="change-plan-upgrade-total">{inr(p.grand_total)}</td></tr>
        </tbody></table>
        <button onClick={onUpgrade} disabled={busy} data-testid="change-plan-upgrade-pay-btn"
          className="mt-3 w-full px-3 py-2.5 rounded-lg text-[13px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 transition-colors">
          {busy ? 'Opening payment…' : `Pay ${inr(p.grand_total)} & upgrade now`}
        </button>
      </div>
    );
  }
  const red = p.reduction || {};
  return (
    <div className="mt-4 rounded-xl border border-slate-200 p-4" data-testid="change-plan-downgrade">
      <div className="text-[12px] font-bold text-violet-700 flex items-center gap-1"><ArrowDownRight size={13} /> Downgrade · takes effect {fmtDate(p.effective_at)}</div>
      {limits}
      <p className="text-[11.5px] text-slate-500 mt-1">You keep your current plan until then. No refund is due because the change happens at period end.</p>
      {!red.fits && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 space-y-1" data-testid="change-plan-reduction-warning">
          {red.messages.map((m, i) => <div key={i} className="flex items-start gap-1.5"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{m}</div>)}
          <div className="text-[11px] text-amber-800/80">If you haven't reduced by then, the downgrade waits and you renew on your current plan.</div>
        </div>
      )}
      <button onClick={onDowngrade} disabled={busy} data-testid="change-plan-downgrade-btn"
        className="mt-3 w-full px-3 py-2.5 rounded-lg text-[13px] font-semibold bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 transition-colors">
        {busy ? 'Scheduling…' : `Schedule downgrade for ${fmtDate(p.effective_at)}`}
      </button>
    </div>
  );
}
